import React, { useEffect, useState } from "react";
import { Search, ChevronLeft, ChevronRight, FileSpreadsheet, Loader2, Users, Wrench } from "lucide-react";
import { toast } from "sonner";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { downloadBlob } from "../../utils/exporters";
import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import DrillUserRow from "./DrillUserRow";

const slug = (s) => (s || "users").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60);
const SIZE = 50;

/** Slide-over listing the users behind a finding. `drill` = { title, description, params, fix?: { label, run } }. */
export default function UserDrillSheet({ scanId, summary, drill, onClose }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setQ("");
    setPage(1);
    setData({ total: 0, items: [] });
  }, [drill]);

  useEffect(() => {
    if (!drill) return undefined;
    const t = setTimeout(() => {
      setLoading(true);
      authApi.scans
        .users(scanId, { ...drill.params, q, page, size: SIZE })
        .then(setData)
        .catch((err) => toast.error("Could not load users", { description: errorMessage(err) }))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [scanId, drill, q, page]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const blob = await authApi.scans.usersCsv(scanId, { ...drill.params, q });
      downloadBlob(blob, `license-hub-${slug(drill.title)}.csv`, "text/csv;charset=utf-8");
      toast.success("Affected users exported as CSV");
    } catch (err) {
      toast.error("Export failed", { description: errorMessage(err) });
    } finally {
      setExporting(false);
    }
  };

  const pages = Math.max(1, Math.ceil(data.total / SIZE));
  return (
    <Sheet open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="drill-sheet w-full sm:max-w-5xl p-0 flex flex-col" data-testid="drill-sheet">
        <SheetHeader className="drill-head">
          <SheetTitle className="drill-title" data-testid="drill-title">
            <Users size={17} aria-hidden="true" /> {drill?.title}
          </SheetTitle>
          <SheetDescription data-testid="drill-description">{drill?.description}</SheetDescription>
        </SheetHeader>
        <div className="drill-body">
          <div className="drill-toolbar">
            <div className="ws-search">
              <Search size={14} />
              <Input value={q} onChange={(e) => (setQ(e.target.value), setPage(1))} placeholder="Search name, UPN, department" data-testid="drill-search" />
            </div>
            <span className="drill-count mono" data-testid="drill-count">
              {loading ? <Loader2 size={13} className="spin" /> : null} {data.total.toLocaleString()} user{data.total === 1 ? "" : "s"}
            </span>
            <button type="button" className="tb-btn sm" onClick={exportCsv} disabled={exporting || !data.total} data-testid="drill-export-csv">
              {exporting ? <Loader2 size={13} className="spin" /> : <FileSpreadsheet size={13} />} CSV
            </button>
            {drill?.fix && (
              <button type="button" className="tb-btn sm primary" onClick={drill.fix.run} data-testid="drill-fix-planner">
                <Wrench size={12} /> {drill.fix.label}
              </button>
            )}
          </div>
          <div className={`ws-table-wrap ${loading ? "loading" : ""}`}>
            <table className="ws-table drill-table" data-testid="drill-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Licenses</th>
                  <th>Missing</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <DrillUserRow key={u.graph_id} user={u} signInAvailable={summary.users.sign_in_available} />
                ))}
                {!loading && data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted center" data-testid="drill-empty">
                      No users match this finding in the selected scan.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="ws-pager">
              <button type="button" className="tb-btn sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="drill-prev">
                <ChevronLeft size={13} /> Prev
              </button>
              <span className="mono">
                {page} / {pages}
              </span>
              <button type="button" className="tb-btn sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="drill-next">
                Next <ChevronRight size={13} />
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
